import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Mic, MicOff, Settings, LogOut, User, MessageSquare, Plus, Sparkles, Volume2, VolumeX, Search, Trash2, Copy, ChevronDown, Image, BarChart3, Play } from 'lucide-react';
import { buildApiUrl } from './config';

// Typing effect component with improved error handling
const TypingEffect = ({ text, onComplete, speed = 30, useMarkdown = false }) => {
  const [displayedText, setDisplayedText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (!text) {
      setDisplayedText('');
      setCurrentIndex(0);
      return;
    }

    if (currentIndex < text.length) {
      const timer = setTimeout(() => {
        setDisplayedText(prev => prev + text[currentIndex]);
        setCurrentIndex(prev => prev + 1);
      }, speed);
      return () => clearTimeout(timer);
    } else if (onComplete) {
      onComplete();
    }
  }, [currentIndex, text, speed, onComplete]);

  // Reset when text changes
  useEffect(() => {
    setDisplayedText('');
    setCurrentIndex(0);
  }, [text]);

  return (
    <div className="whitespace-pre-wrap">
      {useMarkdown ? (
        <div dangerouslySetInnerHTML={{ 
          __html: displayedText
            .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
            .replace(/\*(.*?)\*/g, '<em>$1</em>')
            .replace(/\n/g, '<br>') 
        }} />
      ) : (
        displayedText
      )}
      {currentIndex < text.length && (
        <span className="inline-block w-2 h-5 bg-green-400 ml-1 animate-pulse">|</span>
      )}
    </div>
  );
};

// Animated typing indicator
const TypingIndicator = () => (
  <div className="flex items-center space-x-2">
    <div className="flex space-x-1">
      <div className="w-2 h-2 bg-green-400 rounded-full animate-bounce"></div>
      <div className="w-2 h-2 bg-yellow-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
      <div className="w-2 h-2 bg-red-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
    </div>
    <span className="text-green-200 text-sm">Vibes AI is typing...</span>
  </div>
);

// Search results component
const SearchResults = ({ results, onSelect, onClose }) => (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
    <div className="bg-black/90 backdrop-blur-xl border-2 border-yellow-400/30 rounded-3xl p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-xl font-bold text-yellow-400">Search Results</h3>
        <button onClick={onClose} className="text-gray-400 hover:text-white text-xl">
          ✕
        </button>
      </div>
      
      <div className="overflow-y-auto max-h-[60vh] space-y-3">
        {results.length === 0 ? (
          <p className="text-gray-400 text-center py-8">No results found</p>
        ) : (
          results.map((result, index) => (
            <div 
              key={index}
              className="bg-gray-800/50 rounded-lg p-3 cursor-pointer hover:bg-gray-700/50 transition-colors"
              onClick={() => onSelect(result)}
            >
              <div className="text-sm text-yellow-400 mb-1">{result.conversation_title}</div>
              <div className="text-xs text-gray-400 mb-2">
                {new Date(result.created_at).toLocaleString()}
              </div>
              <div className="text-sm text-gray-300 line-clamp-2">
                {result.content}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  </div>
);

// Settings modal component (revamped)
const SettingsModal = ({ isOpen, onClose, profile, onUpdateProfile }) => {
  const [formData, setFormData] = useState({
    persona: profile?.persona || 'island',
    userContext: profile?.user_context || '',
    useMarkdown: profile?.use_markdown !== false,
    temperature: 0.2,
    topP: 0.95,
  });

  useEffect(() => {
    if (profile) {
      setFormData({
        persona: profile.persona || 'island',
        userContext: profile.user_context || '',
        useMarkdown: profile.use_markdown !== false,
        temperature: 0.2,
        topP: 0.95,
      });
    }
  }, [profile]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    await onUpdateProfile(formData);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-black/90 backdrop-blur-xl border-2 border-yellow-400/30 rounded-3xl p-6 max-w-md w-full mx-4">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-bold text-yellow-400">Settings</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-xl">
            ✕
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Language Style
            </label>
            <select
              value={formData.persona}
              onChange={(e) => setFormData({...formData, persona: e.target.value})}
              className="w-full bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white focus:border-yellow-400 focus:outline-none"
            >
              <option value="island">Island Vibes (Caribbean style)</option>
              <option value="standard">Standard (Clear & direct)</option>
              <option value="informative">Informative (Facts + warmth)</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              About You (Optional)
            </label>
            <textarea
              value={formData.userContext}
              onChange={(e) => setFormData({...formData, userContext: e.target.value})}
              placeholder="Tell me about yourself so I can personalize our conversations..."
              className="w-full bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white focus:border-yellow-400 focus:outline-none h-20 resize-none"
              maxLength={500}
            />
            <div className="text-xs text-gray-400 text-right mt-1">
              {formData.userContext.length}/500
            </div>
          </div>

          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-gray-300">
              Use Markdown Formatting
            </label>
            <button
              type="button"
              onClick={() => setFormData({...formData, useMarkdown: !formData.useMarkdown})}
              className={`w-12 h-6 rounded-full transition-colors ${formData.useMarkdown ? 'bg-yellow-400' : 'bg-gray-600'}`}
            >
              <div className={`w-4 h-4 bg-white rounded-full transition-transform ${formData.useMarkdown ? 'translate-x-6' : 'translate-x-1'}`}></div>
            </button>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Temperature</label>
              <input
                type="number"
                step="0.05"
                min="0"
                max="1"
                value={formData.temperature}
                onChange={(e) => setFormData({...formData, temperature: parseFloat(e.target.value)})}
                className="w-full bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white focus:border-yellow-400 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Top P</label>
              <input
                type="number"
                step="0.05"
                min="0"
                max="1"
                value={formData.topP}
                onChange={(e) => setFormData({...formData, topP: parseFloat(e.target.value)})}
                className="w-full bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white focus:border-yellow-400 focus:outline-none"
              />
            </div>
          </div>

          <button
            type="submit"
            className="w-full bg-gradient-to-r from-yellow-400 to-red-500 text-white font-bold py-3 rounded-xl hover:from-yellow-500 hover:to-red-600 transition-all duration-200"
          >
            Save Settings
          </button>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                      <button
            type="button"
            onClick={() => {
              // Reset preferences functionality
              setFormData({
                persona: 'island',
                userContext: '',
                useMarkdown: true,
                temperature: 0.2,
                topP: 0.95,
              });
            }}
            className="w-full bg-gray-700 text-white font-bold py-3 rounded-xl hover:bg-gray-600 transition-all duration-200"
          >
            Reset Preferences
          </button>
          <button
            type="button"
            onClick={() => {
              // Clear all data functionality
              if (confirm('Are you sure you want to delete all your data? This action cannot be undone.')) {
                // Clear local state
                localStorage.removeItem('vibes_token');
                window.location.reload();
              }
            }}
            className="w-full bg-red-600 text-white font-bold py-3 rounded-xl hover:bg-red-500 transition-all duration-200"
          >
            Delete All My Data
          </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Dynamic greeting component
const DynamicGreeting = ({ username }) => {
  const [greeting, setGreeting] = useState('');

  useEffect(() => {
    const hour = new Date().getHours();
    let timeGreeting = '';
    
    if (hour < 12) {
      timeGreeting = 'Good Morning';
    } else if (hour < 17) {
      timeGreeting = 'Good Afternoon';
    } else {
      timeGreeting = 'Good Evening';
    }

    const greetings = [
      `${timeGreeting}, ${username}!`,
      `Hey ${username}, back at it!`,
      `Welcome back, ${username}!`,
      `${timeGreeting} ${username}! Ready to chat?`,
      `Hey there ${username}! How's it going?`
    ];

    setGreeting(greetings[Math.floor(Math.random() * greetings.length)]);
  }, [username]);

  return (
    <div className="text-center text-yellow-200/70 mb-4">
      <h2 className="text-xl font-bold">{greeting}</h2>
    </div>
  );
};

// Error banner component
const ErrorBanner = ({ message, onClose, onRetry }) => (
  <div className="fixed top-4 left-1/2 transform -translate-x-1/2 bg-red-600 text-white px-6 py-3 rounded-xl shadow-lg z-50 flex items-center space-x-4">
    <span>{message}</span>
    {onRetry && (
      <button onClick={onRetry} className="text-white hover:text-yellow-200 text-sm underline">
        Retry
      </button>
    )}
    <button onClick={onClose} className="ml-4 text-white hover:text-yellow-200 text-lg">✕</button>
  </div>
);

// Confirmation dialog
const ConfirmDialog = ({ open, title, message, onConfirm, onCancel }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-black/90 backdrop-blur-xl border-2 border-yellow-400/30 rounded-3xl p-6 max-w-sm w-full mx-4">
        <h3 className="text-xl font-bold text-yellow-400 mb-4">{title}</h3>
        <p className="text-white mb-6">{message}</p>
        <div className="flex justify-end space-x-4">
          <button onClick={onCancel} className="px-4 py-2 rounded bg-gray-700 text-white hover:bg-gray-600 transition-colors">
            Cancel
          </button>
          <button onClick={onConfirm} className="px-4 py-2 rounded bg-red-600 text-white hover:bg-red-500 transition-colors">
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

// Debug panel removed per production cleanup

export default function VibeChatAI() {
  // [Existing state declarations remain unchanged]
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        return localStorage.getItem('vibes_token');
      }
    } catch (error) {
      console.error('Error accessing localStorage:', error);
    }
    return null;
  });
  const [inputValue, setInputValue] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [modelSpeed, setModelSpeed] = useState('Fast');
  const [isLoading, setIsLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [streamingMessage, setStreamingMessage] = useState('');
  const [isMuted, setIsMuted] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [conversationsLoading, setConversationsLoading] = useState(false);
  const [currentConversation, setCurrentConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [showConversations, setShowConversations] = useState(false);
  const [showSignup, setShowSignup] = useState(false);
  const [authForm, setAuthForm] = useState({
    username: '',
    email: '',
    password: '',
    persona: 'island'
  });
  const [showSettings, setShowSettings] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [errorBanner, setErrorBanner] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState({ open: false, convId: null });
  const [showScrollDown, setShowScrollDown] = useState(false);
  
  // Multimedia features state
  const [showMultimedia, setShowMultimedia] = useState(false);
  const [multimediaType, setMultimediaType] = useState('image_analysis'); // 'image', 'graph', 'tts'
  const [multimediaPrompt, setMultimediaPrompt] = useState('');
  const [multimediaResult, setMultimediaResult] = useState(null);
  const [multimediaLoading, setMultimediaLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [graphOptions, setGraphOptions] = useState({
    dataType: 'sugar_mas_winners',
    chartType: 'bar',
    title: 'Sugar Mas Winners'
  });
  const [ttsOptions, setTTSOptions] = useState({
    voice: 'alloy',
    speed: '1.0'
  });
  // Removed debugMode and networkStatus for production UI

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const chatAreaRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const handleScroll = useCallback(() => {
    if (chatAreaRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = chatAreaRef.current;
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
      setShowScrollDown(!isNearBottom);
    }
  }, []);

  // [Existing useEffect hooks for scroll, debug, error banner, auth check, and health check remain unchanged]
  useEffect(() => {
    scrollToBottom();
  }, [messages, streamingMessage, scrollToBottom]);

  useEffect(() => {
    const chatArea = chatAreaRef.current;
    if (chatArea) {
      chatArea.addEventListener('scroll', handleScroll);
      return () => chatArea.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  // Removed debug hotkey

  useEffect(() => {
    if (errorBanner) {
      const timer = setTimeout(() => setErrorBanner(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [errorBanner]);

  useEffect(() => {
    if (token) {
      setIsAuthenticated(true);
      fetchUserData();
      fetchConversations();
      fetchProfile();
    }
  }, [token]);

  // Removed network status check UI

  // [Existing fetchUserData, fetchProfile, updateProfile, fetchConversations, deleteConversation, etc., remain unchanged]
  const fetchUserData = async () => {
    try {
      if (!token) {
        logout();
        return;
      }
      const tokenParts = token.split('.');
      if (tokenParts.length !== 3) throw new Error('Invalid token format');
      const tokenData = JSON.parse(atob(tokenParts[1]));
      if (tokenData.exp && tokenData.exp < Date.now() / 1000) throw new Error('Token expired');
      setUser({ id: tokenData.user_id, username: tokenData.username });
    } catch (error) {
      console.error('Error fetching user data:', error);
      setErrorBanner('Authentication error. Please sign in again.');
      logout();
    }
  };

  const fetchProfile = async () => {
    try {
      if (!token) return;
      setProfileLoading(true);
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);
      const response = await fetch(buildApiUrl('/api/profile'), {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      if (response.ok) {
        const data = await response.json();
        setProfile(data.profile);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setProfileLoading(false);
    }
  };

  const updateProfile = async (profileData) => {
    try {
      if (!token) return;
      const response = await fetch(buildApiUrl('/api/profile/update'), {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(profileData)
      });
      if (response.ok) await fetchProfile();
      else setErrorBanner('Failed to update profile.');
    } catch (error) {
      setErrorBanner('Network error. Please try again.');
    }
  };

  const clearProfile = async () => {
    try {
      if (!token) return;
      const response = await fetch(buildApiUrl('/api/profile/clear'), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (response.ok) {
        await fetchProfile();
        setErrorBanner('Preferences reset');
      } else {
        setErrorBanner('Failed to reset preferences');
      }
    } catch (error) {
      setErrorBanner('Network error. Please try again.');
    }
  };

  const clearAllData = async () => {
    try {
      if (!token) return;
      const response = await fetch(buildApiUrl('/api/data/clear'), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (response.ok) {
        setConversations([]);
        setMessages([]);
        setCurrentConversation(null);
        await fetchProfile();
        setErrorBanner('All data cleared');
      } else {
        setErrorBanner('Failed to clear data');
      }
    } catch (error) {
      setErrorBanner('Network error. Please try again.');
    }
  };

  const fetchConversations = async (retryCount = 0) => {
    try {
      if (!token) return;
      setConversationsLoading(true);
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);
      const response = await fetch(buildApiUrl('/api/conversations'), {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      if (response.status === 401) {
        logout();
        return;
      }
      if (response.ok) {
        const data = await response.json();
        setConversations(data.conversations || []);
      } else if (retryCount < 2) {
        setTimeout(() => fetchConversations(retryCount + 1), 1000);
      } else {
        setErrorBanner('Failed to fetch conversations.');
      }
    } catch (error) {
      if (retryCount < 2) setTimeout(() => fetchConversations(retryCount + 1), 1000);
      else setErrorBanner('Network error. Please try again.');
    } finally {
      setConversationsLoading(false);
    }
  };

  const confirmDeleteConversation = async () => {
    if (confirmDelete.convId) await deleteConversation(confirmDelete.convId);
    setConfirmDelete({ open: false, convId: null });
  };

  const handleDeleteConversation = (conversationId) => {
    setConfirmDelete({ open: true, convId: conversationId });
  };

  const deleteConversation = async (conversationId) => {
    try {
      if (!token) return;
      const response = await fetch(buildApiUrl(`/api/conversations/delete?conversation_id=${conversationId}`), {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (response.ok) {
        await fetchConversations();
        if (currentConversation?.id === conversationId) {
          setCurrentConversation(null);
          setMessages([]);
        }
      } else {
        setErrorBanner('Failed to delete conversation.');
      }
    } catch (error) {
      setErrorBanner('Network error. Please try again.');
    }
  };

  // [Other existing functions like searchConversations, fetchMessages, handleAuth, logout, etc., remain unchanged]
  const logout = () => {
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
    setConversations([]);
    setMessages([]);
    setCurrentConversation(null);
    setProfile(null);
    try {
      if (typeof window !== 'undefined' && window.localStorage) localStorage.removeItem('vibes_token');
    } catch (error) {
      console.error('Error clearing localStorage:', error);
    }
  };

  // Helpers and missing functions
  const handleCopyMessage = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setErrorBanner('Copied to clipboard');
    } catch (e) {
      setErrorBanner('Failed to copy');
    }
  };

  const startNewConversation = () => {
    setCurrentConversation(null);
    setMessages([]);
  };

  const fetchMessages = async (conversationId) => {
    try {
      if (!token || !conversationId) return;
      const response = await fetch(buildApiUrl(`/api/messages?conversation_id=${conversationId}`), {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (response.status === 401) {
        logout();
        return;
      }
      if (response.ok) {
        const data = await response.json();
        setMessages(data.messages || []);
      }
    } catch (error) {
      setErrorBanner('Failed to load messages.');
    }
  };

  const searchConversations = async () => {
    try {
      if (!token || !searchQuery.trim()) return;
      const response = await fetch(buildApiUrl('/api/conversations/search'), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: searchQuery.trim() })
      });
      if (response.status === 401) {
        logout();
        return;
      }
      if (response.ok) {
        const data = await response.json();
        setSearchResults(data.results || []);
      } else {
        setErrorBanner('Search failed.');
      }
    } catch (error) {
      setErrorBanner('Network error during search.');
    }
  };

  const handleAuth = async (signup) => {
    try {
      setIsLoading(true);
      const endpoint = signup ? '/api/signup' : '/api/signin';
      const payload = signup ? {
        username: authForm.username,
        email: authForm.email,
        password: authForm.password,
        persona: authForm.persona,
      } : {
        username: authForm.username,
        password: authForm.password,
      };
      const response = await fetch(buildApiUrl(endpoint), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (response.ok && data.success) {
        const newToken = data.token;
        setToken(newToken);
        try { localStorage.setItem('vibes_token', newToken); } catch {}
        setIsAuthenticated(true);
        setUser(data.user);
        await fetchProfile();
        await fetchConversations();
      } else {
        setErrorBanner(data.error || 'Authentication failed.');
      }
    } catch (error) {
      setErrorBanner('Network error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!inputValue.trim() || !isAuthenticated || !token) return;
    const messageText = inputValue.trim();
    setInputValue('');

    // If there's an in-progress typing response, finalize it before sending a new message
    if (isTyping && streamingMessage) {
      setMessages(prev => [...prev, { id: Date.now(), role: 'assistant', content: streamingMessage, created_at: new Date().toISOString() }]);
      setIsTyping(false);
      setStreamingMessage('');
    }

    // Optimistically add the user's message immediately
    const userMessage = { id: Date.now(), role: 'user', content: messageText, created_at: new Date().toISOString() };
    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);
    try {
      // Try to include best-effort location if available
      let location = {};
      try {
        if (navigator.geolocation) {
          const pos = await new Promise((res, rej) => navigator.geolocation.getCurrentPosition(res, rej, { maximumAge: 60000, timeout: 2000 }));
          location = { latitude: pos.coords.latitude, longitude: pos.coords.longitude };
        }
      } catch {}

      const response = await fetch(buildApiUrl('/api/chat'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ 
          message: messageText,
          conversation_id: currentConversation?.id || 0,
          use_markdown: profile?.use_markdown !== false,
          location
        })
      });
      if (response.status === 401) {
        logout();
        return;
      }
      const data = await response.json();
      if (response.ok && data.success) {
        setStreamingMessage(data.response || '');
        setIsTyping(true);
        if (!currentConversation && data.conversation_id) {
          const newConv = {
            id: data.conversation_id,
            title: messageText.substring(0, 50),
            created_at: new Date().toISOString(),
            last_updated: new Date().toISOString()
          };
          setCurrentConversation(newConv);
          await fetchConversations();
        }
      } else {
        // Revert input to let user retry
        setInputValue(messageText);
        setErrorBanner(data.error || 'Failed to send message.');
      }
    } catch (error) {
      setInputValue(messageText);
      setErrorBanner('Network error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle image upload for analysis
  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setErrorBanner('Please select a valid image file.');
        return;
      }
      
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setErrorBanner('Image file size must be less than 10MB.');
        return;
      }

      setSelectedImage(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
      
      // Auto-analyze the image
      analyzeUploadedImage(file);
    }
  };

  // Analyze uploaded image
  const analyzeUploadedImage = async (file) => {
    if (!file || !token) return;
    
    setMultimediaLoading(true);
    try {
      // Convert file to base64
      const base64 = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => {
          const base64String = reader.result;
          resolve(base64String);
        };
        reader.readAsDataURL(file);
      });

      const response = await fetch(buildApiUrl('/api/image/analyze'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          image_data: base64,
          prompt: "What do you see in this image? Please describe it in detail.",
          conversation_id: currentConversation?.id || 0
        })
      });

      if (response.status === 401) {
        logout();
        return;
      }

      const data = await response.json();
      if (response.ok && data.success) {
        // Add the analysis to the chat
        const analysisMessage = { 
          id: Date.now(), 
          role: 'assistant', 
          content: `📸 **Image Analysis:**\n\n${data.analysis}`, 
          created_at: new Date().toISOString() 
        };
        setMessages(prev => [...prev, analysisMessage]);
        
        // Clear the image
        setSelectedImage(null);
        setImagePreview(null);
        setErrorBanner(null);
      } else {
        setErrorBanner(data.error || 'Failed to analyze image.');
      }
    } catch (error) {
      setErrorBanner('Network error. Please try again.');
    } finally {
      setMultimediaLoading(false);
    }
  };

  const handleTypingComplete = useCallback(() => {
    // Append the assistant's completed message
    if (streamingMessage) {
      setMessages(prev => [...prev, { id: Date.now(), role: 'assistant', content: streamingMessage, created_at: new Date().toISOString() }]);
    }
    setIsTyping(false);
    setStreamingMessage('');
  }, [streamingMessage]);

  const selectConversation = (conversation) => {
    setCurrentConversation(conversation);
    fetchMessages(conversation.id);
    setShowConversations(false);
  };

  // Multimedia generation functions
  const analyzeImage = async () => {
    if (!multimediaPrompt.trim() || !token) return;
    
    setMultimediaLoading(true);
    try {
      const response = await fetch(buildApiUrl('/api/image/analyze'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          image_url: multimediaPrompt,
          prompt: "What do you see in this image? Please describe it in detail.",
          conversation_id: currentConversation?.id || 0
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setMultimediaResult(data);
        // Add to conversation if there is one
        if (currentConversation && data.success) {
          const message = {
            id: Date.now(),
            role: 'assistant',
            content: `Image Analysis: ${data.analysis}`,
            created_at: new Date().toISOString()
          };
          setMessages(prev => [...prev, message]);
        }
      } else {
        setErrorBanner('Failed to analyze image');
      }
    } catch (error) {
      setErrorBanner('Network error during image analysis');
    } finally {
      setMultimediaLoading(false);
    }
  };

  const generateGraph = async () => {
    if (!graphOptions.title.trim() || !token) return;
    
    setMultimediaLoading(true);
    try {
      const response = await fetch(buildApiUrl('/api/graph/generate'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          data_type: graphOptions.dataType,
          chart_type: graphOptions.chartType,
          title: graphOptions.title,
          data: {},
          conversation_id: currentConversation?.id || 0
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setMultimediaResult(data);
        // Add to conversation if there is one
        if (currentConversation && data.success) {
          const message = {
            id: Date.now(),
            role: 'assistant',
            content: `Generated ${graphOptions.chartType} chart: ${graphOptions.title}`,
            created_at: new Date().toISOString()
          };
          setMessages(prev => [...prev, message]);
        }
      } else {
        setErrorBanner('Failed to generate graph');
      }
    } catch (error) {
      setErrorBanner('Network error during graph generation');
    } finally {
      setMultimediaLoading(false);
    }
  };

  const generateTTS = async (text) => {
    if (!text.trim() || !token) return;
    
    setMultimediaLoading(true);
    try {
      const response = await fetch(buildApiUrl('/api/tts'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          text: text,
          voice: ttsOptions.voice,
          speed: ttsOptions.speed,
          conversation_id: currentConversation?.id || 0
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setMultimediaResult(data);
        // Add to conversation if there is one
        if (currentConversation && data.success) {
          const message = {
            id: Date.now(),
            role: 'assistant',
            content: `Generated speech for: ${text.substring(0, 50)}${text.length > 50 ? '...' : ''}`,
            created_at: new Date().toISOString()
          };
          setMessages(prev => [...prev, message]);
        }
      } else {
        setErrorBanner('Failed to generate speech');
      }
    } catch (error) {
      setErrorBanner('Network error during TTS generation');
    } finally {
      setMultimediaLoading(false);
    }
  };

  // Auth UI (unchanged)
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-purple-900 to-green-900 text-white flex items-center justify-center relative overflow-hidden">
        {errorBanner && (
          <ErrorBanner message={errorBanner} onClose={() => setErrorBanner(null)} />
        )}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 text-6xl animate-pulse">🌺</div>
          <div className="absolute top-40 right-20 text-4xl animate-bounce">🥥</div>
          <div className="absolute bottom-32 left-20 text-5xl animate-pulse">🌴</div>
          <div className="absolute bottom-20 right-32 text-3xl animate-bounce">🦜</div>
          <div className="absolute top-60 left-1/3 text-4xl animate-pulse">🏝️</div>
          <div className="absolute bottom-40 right-1/4 text-5xl animate-bounce">🎭</div>
        </div>
        <div className="bg-black/30 backdrop-blur-xl border-2 border-yellow-400/30 rounded-3xl p-8 max-w-md w-full mx-4 relative z-10">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-r from-yellow-400 via-red-500 to-green-500 rounded-2xl flex items-center justify-center mx-auto mb-4 animate-pulse">
              <span className="text-2xl">🎭</span>
            </div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-yellow-400 via-red-400 to-green-400 bg-clip-text text-transparent">
              Vibes AI
            </h1>
            <p className="text-yellow-200 mt-2">Your Caribbean culture companion 🇰🇳</p>
          </div>
          <div className="space-y-4">
            <input
              type="text"
              placeholder="Username"
              value={authForm.username}
              onChange={(e) => setAuthForm({ ...authForm, username: e.target.value })}
              className="w-full bg-transparent border-2 border-yellow-400/30 rounded-xl p-3 text-white placeholder-yellow-200/70 focus:border-yellow-400 focus:outline-none transition-all duration-200"
            />
            {showSignup && (
              <>
                <input
                  type="email"
                  placeholder="Email"
                  value={authForm.email}
                  onChange={(e) => setAuthForm({ ...authForm, email: e.target.value })}
                  className="w-full bg-transparent border-2 border-yellow-400/30 rounded-xl p-3 text-white placeholder-yellow-200/70 focus:border-yellow-400 focus:outline-none transition-all duration-200"
                />
                <select
                  value={authForm.persona}
                  onChange={(e) => setAuthForm({ ...authForm, persona: e.target.value })}
                  className="w-full bg-transparent border-2 border-yellow-400/30 rounded-xl p-3 text-white focus:border-yellow-400 focus:outline-none transition-all duration-200"
                >
                  <option value="island" className="bg-gray-800">Island Vibes (Caribbean style)</option>
                  <option value="standard" className="bg-gray-800">Standard (Clear & direct)</option>
                  <option value="informative" className="bg-gray-800">Informative (Facts + warmth)</option>
                </select>
              </>
            )}
            <input
              type="password"
              placeholder="Password"
              value={authForm.password}
              onChange={(e) => setAuthForm({ ...authForm, password: e.target.value })}
              className="w-full bg-transparent border-2 border-yellow-400/30 rounded-xl p-3 text-white placeholder-yellow-200/70 focus:border-yellow-400 focus:outline-none transition-all duration-200"
            />
            <button
              onClick={() => handleAuth(showSignup)}
              disabled={isLoading}
              className="w-full bg-gradient-to-r from-yellow-400 to-red-500 text-white font-bold py-3 rounded-xl hover:from-yellow-500 hover:to-red-600 transition-all duration-200 disabled:opacity-50 transform hover:scale-105"
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Loading...
                </div>
              ) : (
                showSignup ? 'Sign Up' : 'Sign In'
              )}
            </button>
            <button
              onClick={() => setShowSignup(!showSignup)}
              className="w-full text-yellow-200 hover:text-yellow-100 transition-colors"
            >
              {showSignup ? 'Already have an account? Sign In' : 'Need an account? Sign Up'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Main Chat Interface with Fixes
  return (
    <>
      {errorBanner && (
        <ErrorBanner 
          message={errorBanner} 
          onClose={() => setErrorBanner(null)}
          onRetry={errorBanner.includes('fetch') ? () => {
            setErrorBanner(null);
            fetchConversations();
          } : undefined}
        />
      )}
      <ConfirmDialog
        open={confirmDelete.open}
        title="Delete Conversation"
        message="Are you sure you want to delete this conversation? This cannot be undone."
        onConfirm={confirmDeleteConversation}
        onCancel={() => setConfirmDelete({ open: false, convId: null })}
      />
      {/* Debug panel removed */}
      
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-purple-900 to-green-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div className="absolute top-20 left-10 text-6xl animate-pulse">🌺</div>
          <div className="absolute top-40 right-20 text-4xl animate-bounce">🥥</div>
          <div className="absolute bottom-32 left-20 text-5xl animate-pulse">🌴</div>
          <div className="absolute bottom-20 right-32 text-3xl animate-bounce">🦜</div>
          <div className="absolute top-60 left-1/3 text-4xl animate-pulse">🏝️</div>
          <div className="absolute bottom-40 right-1/4 text-5xl animate-bounce">🎭</div>
        </div>

        {/* Navigation Header with Fixed Profile Button */}
        <nav className="flex justify-between items-center p-6 relative z-10 bg-black/10 backdrop-blur-sm border-b border-yellow-400/20">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-gradient-to-r from-yellow-400 via-red-500 to-green-500 rounded-lg flex items-center justify-center shadow-lg animate-pulse">
              <span className="text-white text-lg">🇰🇳</span>
            </div>
            <span className="text-xl font-bold bg-gradient-to-r from-yellow-400 via-red-400 to-green-400 bg-clip-text text-transparent">
              Vibes AI
            </span>
          </div>
          
          <div className="flex items-center space-x-2">
            <button 
              onClick={() => setShowMultimedia(true)}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors bg-purple-400/20 border border-purple-400/30"
              title="Multimedia tools"
            >
              <Image className="w-5 h-5" />
            </button>
            
            <button 
              onClick={() => setShowSearch(true)}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors bg-green-400/20 border border-green-400/30"
              title="Search conversations"
            >
              <Search className="w-5 h-5" />
            </button>
            
            <button 
              onClick={() => setShowConversations(!showConversations)}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors relative bg-blue-400/20 border border-blue-400/30"
              title="View conversations"
            >
              <MessageSquare className="w-5 h-5" />
              {conversations.length > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                  {conversations.length > 9 ? '9+' : conversations.length}
                </span>
              )}
            </button>
            
            <button
              onClick={() => setIsMuted(!isMuted)}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              title={isMuted ? 'Unmute' : 'Mute'}
            >
              {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
            </button>
            
            <button
              onClick={() => setShowSettings(true)}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors relative bg-yellow-400/20 border border-yellow-400/30"
              title="Settings & Profile"
              disabled={profileLoading}
            >
              <Settings className="w-5 h-5" />
              {profileLoading && (
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-400 rounded-full animate-pulse"></div>
              )}
            </button>
            
            {/* Debug toggle removed */}
            
            {/* Fixed Profile Button */}
            <div 
              className="flex items-center space-x-2 bg-gradient-to-r from-yellow-500/20 to-red-500/20 rounded-xl px-4 py-2 border border-yellow-400/30 cursor-pointer hover:bg-gradient-to-r hover:from-yellow-500/30 hover:to-red-500/30 transition-colors"
              onClick={() => setShowSettings(true)}
              title="Profile & Settings"
            >
              <User className="w-4 h-4" />
              <span className="text-sm font-medium">{user?.username || 'User'}</span>
            </div>
            
            <button 
              onClick={logout}
              className="p-2 hover:bg-red-500/20 rounded-lg transition-colors text-red-300 hover:text-red-200"
              title="Sign out"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </nav>

        <div className="flex h-[calc(100vh-120px)] relative z-10">
          {/* Conversations Sidebar with Fixed Delete Buttons */}
          {showConversations && (
            <div className="w-80 bg-black/30 backdrop-blur-xl border-r border-yellow-400/30 flex flex-col">
              <div className="flex items-center justify-between p-4 border-b border-yellow-400/20">
                <h3 className="text-lg font-semibold">Conversations</h3>
                <button
                  onClick={startNewConversation}
                  className="p-2 hover:bg-yellow-400/20 rounded-lg transition-colors"
                  title="New conversation"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              
              <div className="flex-1 overflow-y-auto p-4 space-y-2">
                {conversationsLoading ? (
                  <div className="text-center text-yellow-200/70 py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-400 mx-auto mb-2"></div>
                    <p className="font-medium">Loading conversations...</p>
                  </div>
                ) : conversations.length === 0 ? (
                  <div className="text-center text-yellow-200/70 py-8">
                    <MessageSquare className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p className="font-medium">No conversations yet</p>
                    <p className="text-sm mt-1">Start chatting to create your first conversation!</p>
                  </div>
                ) : (
                  conversations.map((conv) => (
                    <div
                      key={conv.id}
                      className={`relative group rounded-lg transition-all duration-200 ${
                        currentConversation?.id === conv.id 
                          ? 'bg-yellow-400/30 border border-yellow-400 shadow-lg' 
                          : 'hover:bg-white/10'
                      }`}
                    >
                      <button
                        onClick={() => selectConversation(conv)}
                        className="w-full text-left p-3 rounded-lg"
                      >
                        <div className="font-medium truncate pr-6">{conv.title}</div>
                        <div className="text-sm text-gray-400 mt-1">
                          {new Date(conv.last_updated).toLocaleDateString()}
                        </div>
                      </button>
                      
                      <button
                        onClick={() => handleDeleteConversation(conv.id)}
                        className="absolute top-2 right-2 opacity-50 hover:opacity-100 transition-opacity p-1 hover:bg-red-500/20 rounded text-red-400 hover:text-red-300"
                        title="Delete conversation"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Main Chat Area */}
          <div className="flex-1 flex flex-col min-w-0">
            <div className="flex-1 overflow-y-auto p-6 space-y-4" ref={chatAreaRef}>
              {messages.length === 0 && !isLoading && (
                <div className="text-center text-yellow-200/70 mt-20">
                  <DynamicGreeting username={user?.username} />
                  <div className="text-6xl mb-4 animate-bounce">🎭</div>
                  <h2 className="text-2xl font-bold mb-2">Welcome to Vibes AI!</h2>
                  <p className="mb-6 text-lg">Ask me about Caribbean culture, Sugar Mas, or anything else!</p>
                </div>
              )}
              
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'} animate-fade-in`}
                >
                  <div
                    className={`max-w-[80%] lg:max-w-[70%] p-4 rounded-2xl relative group ${
                      message.role === 'user'
                        ? 'bg-gradient-to-r from-yellow-400/20 to-red-400/20 border border-yellow-400/30'
                        : 'bg-gradient-to-r from-green-400/20 to-blue-400/20 border border-green-400/30'
                    }`}
                  >
                    <div className="whitespace-pre-wrap break-words">
                      {profile?.use_markdown && message.role === 'assistant' ? (
                        <div dangerouslySetInnerHTML={{ 
                          __html: message.content.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                            .replace(/\*(.*?)\*/g, '<em>$1</em>')
                            .replace(/\n/g, '<br>') 
                        }} />
                      ) : (
                        message.content
                      )}
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <div className="text-xs text-gray-400">
                        {new Date(message.created_at).toLocaleTimeString()}
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => generateTTS(message.content)}
                          className="opacity-30 hover:opacity-100 transition-opacity p-1 hover:bg-white/10 rounded text-green-400 hover:text-green-300"
                          title="Listen to message"
                        >
                          <Play className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleCopyMessage(message.content)}
                          className="opacity-30 hover:opacity-100 transition-opacity p-1 hover:bg-white/10 rounded text-gray-400 hover:text-white"
                          title="Copy message"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              
              {isTyping && streamingMessage && (
                <div className="flex justify-start">
                  <div className="bg-gradient-to-r from-green-400/20 to-blue-400/20 border border-green-400/30 rounded-2xl p-4 max-w-[80%] lg:max-w-[70%]">
                    <TypingEffect 
                      text={streamingMessage} 
                      onComplete={handleTypingComplete}
                      speed={20}
                      useMarkdown={profile?.use_markdown}
                    />
                    <div className="text-xs text-gray-400 mt-2">
                      {new Date().toLocaleTimeString()}
                    </div>
                  </div>
                </div>
              )}
              
              {isLoading && !isTyping && (
                <div className="flex justify-start">
                  <div className="bg-gradient-to-r from-green-400/20 to-blue-400/20 border border-green-400/30 rounded-2xl p-4">
                    <TypingIndicator />
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>

            {showScrollDown && (
              <button
                onClick={scrollToBottom}
                className="fixed bottom-32 right-8 bg-yellow-400 text-black p-3 rounded-full shadow-lg hover:bg-yellow-300 transition-colors z-10"
                title="Scroll to bottom"
              >
                <ChevronDown className="w-5 h-5" />
              </button>
            )}

            <div className="p-6 bg-gradient-to-t from-black/20 to-transparent">
              <div className="bg-black/30 backdrop-blur-xl border-2 border-yellow-400/30 rounded-3xl p-4">
                <form onSubmit={handleSubmit} className="flex items-center space-x-4">
                  <button
                    type="button"
                    onClick={() => setIsListening(!isListening)}
                    className={`p-3 rounded-xl transition-all duration-200 border-2 flex-shrink-0 ${
                      isListening 
                        ? 'bg-red-500 text-white shadow-lg shadow-red-500/50 border-red-400 animate-pulse' 
                        : 'hover:bg-green-500/20 border-green-400/50 hover:scale-110'
                    }`}
                    title={isListening ? 'Stop listening' : 'Start voice input'}
                  >
                    {isListening ? <Mic className="w-5 h-5" /> : <MicOff className="w-5 h-5" />}
                  </button>
                  
                  {/* Image Upload Button */}
                  <input
                    type="file"
                    id="image-upload"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                  <button
                    type="button"
                    onClick={() => document.getElementById('image-upload').click()}
                    className="p-3 rounded-xl transition-all duration-200 border-2 flex-shrink-0 hover:bg-purple-500/20 border-purple-400/50 hover:scale-110"
                    title="Upload image for analysis"
                  >
                    <Image className="w-5 h-5 text-purple-400" />
                  </button>
                  <input
                    ref={inputRef}
                    type="text"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    placeholder="Ask me bout Caribbean culture, Sugar Mas, or type 'menu' for options... 🎭"
                    className="flex-1 bg-transparent text-white placeholder-yellow-200/70 text-lg focus:outline-none min-w-0"
                    maxLength={1000}
                  />
                  <div className="flex items-center space-x-3 flex-shrink-0">
                    <div className="flex items-center space-x-2 bg-gradient-to-r from-yellow-500/20 to-red-500/20 rounded-xl px-4 py-3 border border-yellow-400/30">
                      <Sparkles className="w-4 h-4 text-yellow-400" />
                      <select 
                        value={modelSpeed}
                        onChange={(e) => setModelSpeed(e.target.value)}
                        className="bg-transparent text-sm focus:outline-none cursor-pointer text-yellow-200"
                        title="Select response style"
                      >
                        <option value="Fast" className="bg-gray-800 text-white">Fast</option>
                        <option value="Cultural" className="bg-gray-800 text-white">Cultural</option>
                        <option value="Storyteller" className="bg-gray-800 text-white">Storyteller</option>
                      </select>
                    </div>
                    <button
                      type="submit"
                      disabled={!inputValue.trim()}
                      className="bg-gradient-to-r from-yellow-400 to-red-500 text-white px-6 py-3 rounded-xl font-medium hover:from-yellow-500 hover:to-red-600 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105 flex-shrink-0"
                    >
                      Send
                    </button>
                  </div>
                </form>
                <div className="text-xs text-gray-400 text-right mt-2">
                  {inputValue.length}/1000
                </div>
                
                {/* Image Upload Status */}
                {selectedImage && (
                  <div className="mt-3 p-3 bg-purple-500/20 border border-purple-400/30 rounded-lg">
                    <div className="flex items-center space-x-3">
                      {imagePreview && (
                        <img 
                          src={imagePreview} 
                          alt="Preview" 
                          className="w-12 h-12 object-cover rounded-lg"
                        />
                      )}
                      <div className="flex-1">
                        <div className="text-sm text-purple-300 font-medium">
                          {selectedImage.name}
                        </div>
                        <div className="text-xs text-purple-200">
                          {multimediaLoading ? 'Analyzing image...' : 'Image ready for analysis'}
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          setSelectedImage(null);
                          setImagePreview(null);
                          document.getElementById('image-upload').value = '';
                        }}
                        className="text-purple-300 hover:text-white"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {showSearch && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-black/90 backdrop-blur-xl border-2 border-yellow-400/30 rounded-3xl p-6 max-w-md w-full mx-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-yellow-400">Search Conversations</h3>
                <button 
                  onClick={() => {
                    setShowSearch(false);
                    setSearchQuery('');
                    setSearchResults([]);
                  }} 
                  className="text-gray-400 hover:text-white text-xl"
                >
                  ✕
                </button>
              </div>
              <div className="space-y-4">
                <input
                  type="text"
                  placeholder="Search for messages..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && searchConversations()}
                  className="w-full bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white focus:border-yellow-400 focus:outline-none"
                  autoFocus
                />
                <button
                  onClick={searchConversations}
                  disabled={!searchQuery.trim()}
                  className="w-full bg-gradient-to-r from-yellow-400 to-red-500 text-white font-bold py-2 rounded-lg hover:from-yellow-500 hover:to-red-600 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Search
                </button>
              </div>
            </div>
          </div>
        )}

        {searchResults.length > 0 && (
          <SearchResults 
            results={searchResults} 
            onSelect={(result) => {
              const conversation = conversations.find(c => c.id === result.conversation_id);
              if (conversation) selectConversation(conversation);
              setShowSearch(false);
              setSearchResults([]);
              setSearchQuery('');
            }} 
            onClose={() => {
              setSearchResults([]);
              setSearchQuery('');
            }} 
          />
        )}

        {/* Multimedia Modal */}
        {showMultimedia && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-black/90 backdrop-blur-xl border-2 border-purple-400/30 rounded-3xl p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold text-purple-400">🎨 Multimedia Tools</h3>
                <button 
                  onClick={() => {
                    setShowMultimedia(false);
                    setMultimediaResult(null);
                    setMultimediaPrompt('');
                  }} 
                  className="text-gray-400 hover:text-white text-xl"
                >
                  ✕
                </button>
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Image Analysis */}
                <div className="bg-gray-800/50 rounded-xl p-4 border border-purple-400/30">
                  <div className="flex items-center space-x-2 mb-4">
                    <Image className="w-5 h-5 text-purple-400" />
                    <h4 className="text-lg font-semibold text-purple-400">Image Analysis</h4>
                  </div>
                  
                  <textarea
                    value={multimediaPrompt}
                    onChange={(e) => setMultimediaPrompt(e.target.value)}
                    placeholder="Enter image URL to analyze..."
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:border-purple-400 focus:outline-none h-24 resize-none mb-4"
                    maxLength={500}
                  />
                  
                  <button
                    onClick={analyzeImage}
                    disabled={!multimediaPrompt.trim() || multimediaLoading}
                    className="w-full bg-gradient-to-r from-purple-400 to-pink-500 text-white font-bold py-2 rounded-lg hover:from-purple-500 hover:to-pink-600 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {multimediaLoading ? 'Analyzing...' : 'Analyze Image'}
                  </button>
                </div>

                {/* Graph Generation */}
                <div className="bg-gray-800/50 rounded-xl p-4 border border-blue-400/30">
                  <div className="flex items-center space-x-2 mb-4">
                    <BarChart3 className="w-5 h-5 text-blue-400" />
                    <h4 className="text-lg font-semibold text-blue-400">Data Visualization</h4>
                  </div>
                  
                  <div className="space-y-3 mb-4">
                    <select
                      value={graphOptions.dataType}
                      onChange={(e) => setGraphOptions({...graphOptions, dataType: e.target.value})}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:border-blue-400 focus:outline-none"
                    >
                      <option value="sugar_mas_winners">Sugar Mas Winners</option>
                      <option value="culturama_winners">Culturama Winners</option>
                      <option value="caribbean_music">Caribbean Music</option>
                      <option value="sugar_mas_timeline">Sugar Mas Timeline</option>
                    </select>
                    
                    <select
                      value={graphOptions.chartType}
                      onChange={(e) => setGraphOptions({...graphOptions, chartType: e.target.value})}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:border-blue-400 focus:outline-none"
                    >
                      <option value="bar">Bar Chart</option>
                      <option value="line">Line Chart</option>
                      <option value="pie">Pie Chart</option>
                      <option value="scatter">Scatter Plot</option>
                      <option value="histogram">Histogram</option>
                    </select>
                    
                    <input
                      type="text"
                      value={graphOptions.title}
                      onChange={(e) => setGraphOptions({...graphOptions, title: e.target.value})}
                      placeholder="Chart title..."
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:border-blue-400 focus:outline-none"
                    />
                  </div>
                  
                  <button
                    onClick={generateGraph}
                    disabled={!graphOptions.title.trim() || multimediaLoading}
                    className="w-full bg-gradient-to-r from-blue-400 to-cyan-500 text-white font-bold py-2 rounded-lg hover:from-blue-500 hover:to-cyan-600 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {multimediaLoading ? 'Generating...' : 'Generate Chart'}
                  </button>
                </div>

                {/* Text-to-Speech */}
                <div className="bg-gray-800/50 rounded-xl p-4 border border-green-400/30">
                  <div className="flex items-center space-x-2 mb-4">
                    <Play className="w-5 h-5 text-green-400" />
                    <h4 className="text-lg font-semibold text-green-400">Text-to-Speech</h4>
                  </div>
                  
                  <div className="space-y-3 mb-4">
                    <select
                      value={ttsOptions.voice}
                      onChange={(e) => setTTSOptions({...ttsOptions, voice: e.target.value})}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:border-green-400 focus:outline-none"
                    >
                      <option value="alloy">Alloy</option>
                      <option value="echo">Echo</option>
                      <option value="fable">Fable</option>
                      <option value="onyx">Onyx</option>
                      <option value="nova">Nova</option>
                      <option value="shimmer">Shimmer</option>
                    </select>
                    
                    <select
                      value={ttsOptions.speed}
                      onChange={(e) => setTTSOptions({...ttsOptions, speed: e.target.value})}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:border-green-400 focus:outline-none"
                    >
                      <option value="0.25">0.25x (Slow)</option>
                      <option value="0.5">0.5x</option>
                      <option value="0.75">0.75x</option>
                      <option value="1.0">1.0x (Normal)</option>
                      <option value="1.25">1.25x</option>
                      <option value="1.5">1.5x</option>
                      <option value="2.0">2.0x (Fast)</option>
                    </select>
                  </div>
                  
                  <p className="text-sm text-gray-400 mb-3">
                    Click the play button on any message to convert it to speech
                  </p>
                </div>
              </div>

              {/* Results Display */}
              {multimediaResult && (
                <div className="mt-6 p-4 bg-gray-800/50 rounded-xl border border-gray-600">
                  <h4 className="text-lg font-semibold text-white mb-3">Generated Result:</h4>
                  
                  {multimediaType === 'image_analysis' && multimediaResult.analysis && (
                    <div className="text-center">
                      <div className="bg-gray-700 p-4 rounded-lg text-left">
                        <h5 className="text-lg font-semibold text-white mb-2">Analysis Result:</h5>
                        <p className="text-gray-300 whitespace-pre-wrap">{multimediaResult.analysis}</p>
                      </div>
                      <p className="text-sm text-gray-400 mt-2">
                        Image URL: {multimediaResult.image_url}
                      </p>
                    </div>
                  )}
                  
                  {multimediaType === 'graph' && multimediaResult.image_url && (
                    <div className="text-center">
                      <iframe
                        src={multimediaResult.image_url}
                        title={multimediaResult.title}
                        className="w-full h-96 border-0 rounded-lg"
                      />
                      <p className="text-sm text-gray-400 mt-2">
                        {multimediaResult.chart_type} chart: {multimediaResult.title}
                      </p>
                    </div>
                  )}
                  
                  {multimediaType === 'tts' && multimediaResult.audio_url && (
                    <div className="text-center">
                      <audio controls className="w-full">
                        <source src={multimediaResult.audio_url} type="audio/mpeg" />
                        Your browser does not support the audio element.
                      </audio>
                      <p className="text-sm text-gray-400 mt-2">
                        Voice: {multimediaResult.voice}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Disclaimers */}
              <div className="mt-6 text-xs text-gray-500 space-y-2">
                <p>⚠️ <strong>Image Analysis:</strong> Image analysis is performed using AI and may not always be 100% accurate.</p>
                <p>⚠️ <strong>Text-to-Speech:</strong> Audio is generated using AI technology and may contain imperfections.</p>
                <p>⚠️ <strong>Data Visualization:</strong> Charts are generated from provided or sample data for demonstration purposes.</p>
              </div>
            </div>
          </div>
        )}

        <SettingsModal 
          isOpen={showSettings}
          onClose={() => setShowSettings(false)}
          profile={profile}
          onUpdateProfile={updateProfile}
        />
      </div>
    </>
  );
}